import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { DynamicComponent, DynamicIoDirective } from 'ng-dynamic-component';
import { filter, from, switchMap } from 'rxjs';
import { StoryblokComponentInstance } from '../../../interfaces/storyblok';
import { StoryblokLoadComponentsService } from '../../../services/storyblok-load-components.service';
import { PictureComponent } from '../../../shared/components/picture/picture.component';
import { AnimateWhenInViewDirective } from '../../../shared/directives/animate-when-in-view.directive';
import { BlockStyle } from '../../interfaces/block-style';
import { StoryblokCdnPipe } from '../../pipes/storyblok-cdn.pipe';
import { StoryblokCta, StoryblokImage } from '../../storyblok-types';
import { StoryblokDirective } from '../../storyblok.directive';
import { AnimationHostComponent } from '../animation/animation-host.component';
import { AnimationKey, getAnimationDefinition, resolveAnimationSize } from '../animation/animation-registry';
import { BlockWrapperComponent, getPageWidth } from '../block-wrapper/block-wrapper.component';
import { TeaserAssetLoading, TeaserAssetLoadingStore } from './teaser-asset-loading';

export type TeaserAsset = {
  _uid?: string;
  component?: string;
  _editable?: string;
  image?: StoryblokImage;
  poster?: StoryblokImage;
  video?: StoryblokImage;
  /** Code-managed animation key (`animation` / `teaser_animation` nests) */
  animation?: string;
  width?: number | string;
  height?: number | string;
};

@Component({
  selector: 'app-teaser',
  templateUrl: './teaser.component.html',
  styleUrls: ['./teaser.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,
    PictureComponent,
    AnimateWhenInViewDirective,
    RouterLink,
    StoryblokDirective,
    DynamicComponent,
    DynamicIoDirective,
    BlockWrapperComponent,
    StoryblokCdnPipe,
    AnimationHostComponent,
  ],
})
export class TeaserComponent implements OnChanges {
  private storyblokLoadComponentsService = inject(StoryblokLoadComponentsService);
  @Input() _uid: string; // just here to prevent errors
  @Input() component: string; // just here to prevent errors
  @Input() _editable: string;
  readonly style = input<BlockStyle[]>(undefined);

  @Input() assetOutOfFrame: boolean;
  @Input() assetSize: string;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  readonly _asset = input<TeaserAsset>(undefined, { alias: 'asset' });
  readonly asset = computed(() => {
    const asset = this._asset();
    if (Array.isArray(asset)) {
      return asset[0];
    }
    return asset;
  });

  @Input() assetBackground: StoryblokImage;
  private readonly assetLoadingStore = inject(TeaserAssetLoadingStore, { optional: true });
  /** Decided by storyblok-root from this teaser's position in the page; `eager` outside a page. */
  private readonly assetLoading = computed<TeaserAssetLoading>(
    () => this.assetLoadingStore?.modes().get(this._uid) ?? 'eager'
  );
  readonly priorityImage = computed(() => this.assetLoading() === 'priority');
  readonly deferAsset = computed(() => this.assetLoading() === 'deferred');
  @Input() content: StoryblokComponentInstance[];
  @Input() assetPosition: 'left' | 'right' = 'right';
  @Input() shadowBox: boolean;
  @Input() borderRadius: string;
  @Input() hideAssetOnMobile: boolean;

  readonly maxWidth = computed(() => {
    const style = this.style();
    if (!style) return;
    return getPageWidth(style?.[0]?.pageWidth, style?.[0]?.customPageWidth);
  });

  components = this.storyblokLoadComponentsService.components;

  readonly hasAnimation = computed(() => !!this.asset()?.animation);

  private readonly animationDefinition = toSignal(
    toObservable(this.asset).pipe(
      filter(asset => !!asset?.animation),
      switchMap(asset => from(getAnimationDefinition(asset.animation as AnimationKey)))
    )
  );

  private readonly imageUrl = computed(() => {
    const asset = this.asset();
    if (!asset) return;
    if (asset.video) {
      return asset.poster?.filename;
    }
    return asset.image?.filename;
  });

  readonly dimensions = computed<{
    width: number;
    height: number;
  }>(() => {
    const url = this.imageUrl();
    const hasAnimation = this.hasAnimation();
    const animationDefinition = this.animationDefinition();
    const asset = this.asset();

    if (!url || !asset) {
      return {
        width: 1000,
        height: 1000,
      };
    }

    if (hasAnimation) {
      return resolveAnimationSize(animationDefinition, asset.width, asset.height);
    }

    return {
      width: parseInt(url.split('/')[5]?.split('x')[0]),
      height: parseInt(url.split('/')[5]?.split('x')[1]),
    };
  });

  cta: StoryblokCta;

  ngOnChanges(): void {
    this.content.forEach(item => {
      if (!('style' in item)) return;
      if (!item.style) return;
      if (Array.isArray(item.style) && !item.style.length) {
        item.style = [{} as BlockStyle];
      }
      item.style[0].inWrapper = true;
    });
  }
}
